import { useState } from "react";
import { Box, Chip, Stack, Typography } from "@mui/material";
import { format, isValid } from "date-fns";
import { SectionPaper } from "@/components/Paper";
import { SectionTitle } from "@/components/Title";
import { Divider } from "@/components/Divider";
import {
  Button,
  ButtonColorOption,
  ButtonVariantOption,
} from "@/components/Button";
import { SizingWrapperStyle } from "@/components/Wrapper";
import { ConfirmDialog } from "@/components/Dialog";
import { useFeatureFlags } from "@/configs/featureFlag";
import { useSalesforceConnection } from "@/features/salesforceIntegration/hooks/useSalesforceConnection";
import { type SalesforceConnectionStatus } from "@/features/salesforceIntegration/types";

const STATUS_LABEL: Record<SalesforceConnectionStatus, string> = {
  NOT_CONNECTED: "未接続",
  CONNECTED: "接続済み",
  NEEDS_REAUTHORIZATION: "再認証が必要",
};

const STATUS_DESCRIPTION: Record<SalesforceConnectionStatus, string> = {
  NOT_CONNECTED: "Salesforceと連携していません。",
  CONNECTED: "Salesforceと連携しています。",
  NEEDS_REAUTHORIZATION:
    "連携が30日間使用されなかったため、認証が失効しました。もう一度連携してください。",
};

/**
 * ユーザー設定画面のSalesforce連携セクション。
 * `hasContract && redataOnSalesforce`のときのみ表示する（実装計画書6.2節・7.1節）。
 * 操作できるのはログイン中のユーザー自身の接続のみで、管理者ロールによる制限は設けない。
 */
export const SalesforceConnectionSection: React.FC = () => {
  const { redataOnSalesforce } = useFeatureFlags();
  const {
    connection,
    connectionStatus,
    startAuthorization,
    isStartingAuthorization,
    disconnect,
    isDisconnecting,
  } = useSalesforceConnection({ enabled: redataOnSalesforce === true });
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  if (!redataOnSalesforce || connection?.hasContract !== true) {
    return null;
  }

  const handleConfirmDisconnect = (): void => {
    setIsConfirmOpen(false);
    void disconnect();
  };

  // 認証日時はバックエンド由来の文字列で、不正な値だと`format`が例外を投げ画面全体が落ちる。
  // 妥当な日時に限って整形し、それ以外は非表示にして描画を守る。
  const authorizedAtDate = connection.authorizedAt
    ? new Date(connection.authorizedAt)
    : null;
  const formattedAuthorizedAt =
    authorizedAtDate && isValid(authorizedAtDate)
      ? format(authorizedAtDate, "yyyy/MM/dd HH:mm")
      : null;

  return (
    <SectionPaper>
      <SectionTitle>Salesforce連携</SectionTitle>
      <Divider />
      <Box sx={{ m: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <Chip
            label={STATUS_LABEL[connectionStatus]}
            color={connectionStatus === "CONNECTED" ? "success" : "default"}
            size="small"
          />
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {STATUS_DESCRIPTION[connectionStatus]}
        </Typography>

        {connectionStatus === "CONNECTED" && connection && (
          <Stack spacing={0.5} sx={{ mb: 2 }}>
            <Typography variant="body2">
              接続先組織ID: {connection.salesforceOrgId}
            </Typography>
            {formattedAuthorizedAt && (
              <Typography variant="body2">
                認証日時: {formattedAuthorizedAt}
              </Typography>
            )}
          </Stack>
        )}

        <Stack direction="row" spacing={2}>
          {connectionStatus !== "CONNECTED" && (
            <Button
              label="Salesforceと連携する"
              variant={ButtonVariantOption.Contained}
              size={SizingWrapperStyle.SMALL}
              disabled={isStartingAuthorization}
              onClick={() => {
                void startAuthorization();
              }}
            />
          )}
          {connectionStatus === "CONNECTED" && (
            <Button
              label="連携を解除する"
              variant={ButtonVariantOption.Outlined}
              color={ButtonColorOption.Error}
              size={SizingWrapperStyle.SMALL}
              disabled={isDisconnecting}
              onClick={() => {
                setIsConfirmOpen(true);
              }}
            />
          )}
        </Stack>
      </Box>

      <ConfirmDialog
        open={isConfirmOpen}
        title="Salesforce連携の解除"
        message="Salesforceとの連携を解除します。よろしいですか？"
        confirmLabel="解除する"
        confirmButtonColorOption={ButtonColorOption.Error}
        onConfirm={handleConfirmDisconnect}
        onCancel={() => {
          setIsConfirmOpen(false);
        }}
      />
    </SectionPaper>
  );
};
